"use client";

/**
 * Couche transversale de navigation avec View Transitions (spec 11).
 *
 * React 19.2.4 (pinné dans ce projet) n'exporte pas encore le composant
 * `ViewTransition` documenté par les guides Next.js 16 — celui-ci est réservé
 * aux builds Canary de React. On pilote donc directement l'API navigateur
 * `document.startViewTransition`, en couche strictement additive : sur un
 * appareil qui ne la supporte pas, la navigation Next.js standard s'exécute
 * à l'identique (cf. contrainte "parc matériel" de la spec).
 */

import { startTransition } from "react";

type Push = (href: string) => void;

let pendingResolve: (() => void) | null = null;
let pendingTimeoutId: number | null = null;

function clearPending() {
  if (pendingTimeoutId !== null) {
    window.clearTimeout(pendingTimeoutId);
    pendingTimeoutId = null;
  }
  pendingResolve = null;
}

/** À appeler après chaque commit de page (cf. ViewTransitionSettler) pour
 * signaler à la transition en cours que le nouveau contenu est affiché. */
export function settleViewTransition() {
  if (!pendingResolve) return;
  const resolve = pendingResolve;
  clearPending();
  resolve();
}

export function navigateWithViewTransition(push: Push, href: string, types: string[] = []) {
  if (typeof document === "undefined" || !document.startViewTransition) {
    push(href);
    return;
  }

  // Une transition serait déjà en attente (double-clic rapide) : on la
  // résout immédiatement pour ne pas bloquer la suivante.
  settleViewTransition();

  const transition = document.startViewTransition(() => {
    return new Promise<void>((resolve) => {
      pendingResolve = resolve;
      // Filet de sécurité : si le nouveau contenu ne "settle" jamais
      // (navigation annulée, route identique...), on ne bloque pas
      // indéfiniment l'affichage derrière l'ancien instantané.
      pendingTimeoutId = window.setTimeout(() => {
        pendingResolve = null;
        pendingTimeoutId = null;
        resolve();
      }, 1500);

      startTransition(() => {
        push(href);
      });
    });
  });

  // Un abort est un cas normal (reload forcé, onglet caché, navigation
  // annulée...) : on l'avale pour ne pas le faire remonter comme une
  // erreur runtime dans l'overlay Next.js.
  transition.finished.catch(() => {});

  // `ViewTransitionTypeSet` se comporte comme un `Set<string>` au runtime,
  // mais lib.dom.d.ts n'expose que `forEach` — cast local, feature testé.
  const typeSet = transition.types as unknown as Set<string> | undefined;
  for (const type of types) {
    typeSet?.add?.(type);
  }
}
